import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MessageService } from './message.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { ProgramIncrement } from '../models/programIncrement';
import { map } from 'rxjs/operators';

/**
 * this class represents access to all rest requests concerning Project data of the PyWall
 * this rest service honors isMock from settingservice
 */
@Injectable()
export class ProgramIncrementService {

  private programIncrementBaseURL: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private configService: ConfigLoaderService
  ) {

    const pyWallUrl = this.configService.settings.pyWallServer.url;
    this.programIncrementBaseURL = `${pyWallUrl}programincrements`;
  }

  /**
   * this returns all details to a specific project
   * @param {string} programId
   * @returns {Observable<Project>}
   */
  getProgramIncrementsByProgram(programId: number): Observable<ProgramIncrement[]> {
    this.messageService.sendDebug(`ProgramIncrementService getProgramIncrementByProgram on ${programId} called.`);
    const targetURL = `${this.programIncrementBaseURL}/?programId=${programId}`;
    const apiResult: Observable<ProgramIncrement[]> = this.http.get<ProgramIncrement[]>(targetURL);
    const mappedResult = apiResult.pipe(map(
      (increments: ProgramIncrement[]) => increments.map(increment => this.mapJSONToProgramIncrement(increment))
    ));
    return mappedResult;
  }

  create(data: any): Observable<any> {
    const payload = {
      name: data.name,
      goal: data.goal,
      date_start: data.start.toISOString(),
      number_of_sprints: data.numberOfSprints,
      sprint_duration: data.sprintDuration,
      sprint_prefix: data.sprintPrefix,
      program_id: data.programId,
    };

    this.messageService.sendDebug('ProgramIncrementService create called');
    const targetURL = this.programIncrementBaseURL;
    // TODO the result of this should probably be mapped to a real object too
    return this.http.post<ProgramIncrement>(`${targetURL}/`, payload);
  }

  delete(programIncrementId: number, programId: number) {
    this.messageService.sendDebug('ProgramIncrementService delete called');
    const targetURL = this.programIncrementBaseURL;
    return this.http.delete(`${targetURL}/${programIncrementId}/?programId=${programId}`);
  }

  getProgramIncrementById(programIncrementId: number, programId: number): Observable<ProgramIncrement> {
    this.messageService.sendDebug(`ProgramIncrementService getProgramIncrementByKey on ${programIncrementId} called.`);
    const targetURL = `${this.programIncrementBaseURL}/${programIncrementId}/?programId=${programId}`;
    return this.http.get<ProgramIncrement>(targetURL).pipe(map(jsonData => this.mapJSONToProgramIncrement(jsonData)));
  }

  sortByDate(programIncrements: ProgramIncrement[], descending: boolean): ProgramIncrement[] {
    return programIncrements.sort((a, b) => {
      if (a.dateStart > b.dateStart) {
        return descending ? -1 : 1;
      } else if (a.dateStart < b.dateStart) {
        return descending ? 1 : -1;
      }
      return 0;
    });
  }

  /**
   * Create a real ProgramIncrement-Object from pyWall-json-data
   */
  private mapJSONToProgramIncrement(jsonData: any): ProgramIncrement {
    const programIncrement = new ProgramIncrement(
      jsonData.id,
      jsonData.name,
      jsonData.goal,
      jsonData.date_start,
      jsonData.number_of_sprints,
      jsonData.sprint_duration,
      jsonData.sprint_prefix,
      jsonData.program_id
    );
    return programIncrement;
  }

}
